import { Component, OnChanges, OnInit, SimpleChanges, inject, input } from '@angular/core';
import { MatTabNavPanel, MatTabNav, MatTabLink } from '@angular/material/tabs';
import { ResearchUnitsService } from '../research-units.service';
import { SearchEntities } from '../../shared/configurations/portal-search.constant';
import { State } from '../../shared/configurations/state.constant';
import { EsploroEntity } from '../../shared/interfaces/esploro-entity';
import { DiscoveryService } from '../../discovery/discovery.service';
import { Router, UrlTree, RouterLink } from '@angular/router';
import { combineLatest, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatTooltip } from '@angular/material/tooltip';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'esp-research-unit-nav',
    templateUrl: './research-unit-nav.component.html',
    styleUrls: ['./research-unit-nav.component.scss'],
    imports: [
        MatTabNav,
        MatTabLink,
        MatTooltip,
        RouterLink,
        AsyncPipe,
        TranslateModule
    ]
})
export class ResearchUnitNavComponent implements OnInit, OnChanges {
    private unitsSvc = inject(ResearchUnitsService);
    private discoSvc = inject(DiscoveryService);
    private router = inject(Router);

    readonly activeEntity = input<string>(undefined);
    readonly unitCode = input<string>(undefined);
    readonly disabledTabs = input<string[]>([]);
    readonly tabPanel = input<MatTabNavPanel>(undefined);
    readonly hideCounts = input(true);

    sections: EsploroEntity[];
    sectionCounts$: { [k: string]: Observable<number> };
    urlTreeMap: { [k: string]: UrlTree };

    _labelOverrideMap = { researchers: 'profiles' };
    _allDisabledTabs: string[] = [];

    constructor() {
        const unitsSvc = this.unitsSvc;

        this.sections = unitsSvc.configSections;
    }

    ngOnInit() {
        this.mapUrlTrees();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['unitCode'] && changes['unitCode'].currentValue) {
            this.disableEmptyTabs();
        }
    }

    disableEmptyTabs() {
        this.sectionCounts$ = {
            outputs: this.discoSvc.getUnitAssetsCount(this.unitCode()),
            researchers: this.discoSvc.getUnitResearchersCount(this.unitCode()),
            projects: this.discoSvc.getUnitProjectsCount(this.unitCode()),
            homepage: this.unitsSvc
                .getResearchUnitByCode(this.unitCode())
                .pipe(map((unitData) => (unitData.displayHomePage ? 1 : 0))),
        };
        combineLatest(this.sectionCounts$).subscribe((counts) => {
            this._allDisabledTabs = [...this.disabledTabs()];
            for (const entityKey in counts) {
                if (counts[entityKey] <= 0) {
                    this._allDisabledTabs.push(entityKey);
                }
            }
            if (this._allDisabledTabs.includes(this.activeEntity())) {
                this.rerouteToActiveTab();
            }
        });
    }

    unitLinkQueryParams(entity) {
        return this.unitsSvc.unitLinkQueryParams(this.unitCode(), entity);
    }

    unitSearchLink(entity) {
        switch (entity) {
            case SearchEntities.RESEARCHERS_ENTITY:
                return [State.RESEARCHERS_SEARCH];
            case SearchEntities.OUTPUTS_ENTITY:
                return [State.OUTPUTS_SEARCH];
            case SearchEntities.PROJECTS_ENTITY:
                return [State.PROJECTS_SEARCH];
            default:
                return ['/research-units/', this.unitCode()];
        }
    }

    mapUrlTrees() {
        this.urlTreeMap = {};
        for (let entity of this.sections) {
            const tabKey = entity.code?.substring(4);
            this.urlTreeMap[tabKey] = this.router.createUrlTree(
                this.unitSearchLink(tabKey),
                {
                    queryParams: this.unitLinkQueryParams(tabKey),
                },
            );
        }
    }

    rerouteToActiveTab() {
        let redirectUrlTree;
        for (let tabKey of this.unitsSvc.defaultSections) {
            if (
                !this._allDisabledTabs.includes(tabKey) &&
                redirectUrlTree === undefined
            ) {
                redirectUrlTree = this.urlTreeMap[tabKey];
            }
        }

        // if we still haven't found anything in order of defaults, redirect to first enabled tab
        for (let entity of this.sections) {
            const tabKey = entity.code?.substring(4);
            if (
                !this._allDisabledTabs.includes(tabKey) &&
                redirectUrlTree === undefined
            ) {
                redirectUrlTree = this.urlTreeMap[tabKey];
            }
        }
        if (redirectUrlTree !== undefined) {
            this.router.navigateByUrl(redirectUrlTree);
        }
    }
}
